import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  executeAssistantAction,
  getAssistantActionCatalog,
  isAssistantActionName,
} from '../lib/assistant/appActions';
import { captureScreenshot } from '../lib/assistant/screenCapture';
import { GHOST_CHOICE_EVENT, type GhostChoice } from '../lib/assistant/customerLookup';
import { hub, watchSession } from '../components/agents/api';
import type { LabEvent } from '../components/agents/types';
import { playRemoteVoice, playVoiceChime, setRemoteVoiceMuted } from '../lib/voiceChime';
import { markSalesCardWorking, setSalesCardCall, settleSalesCard, showSalesCardUpdating } from '../lib/salesCardWork';

/**
 * Live voice conversation with Nexus, the business's general manager (OpenAI
 * Realtime, WebRTC). The session survives in-app navigation: the provider sits
 * above the router, so the user can talk while moving around the app. A
 * viewport outline pulses with the assistant's voice while the session is active.
 *
 * How-it-works questions are answered from the support docs (lookup_help).
 * Live data and changes are handed to the same agent as a typed chat
 * (delegate_task). The voice stays up while the agent works, pauses the mic
 * after a stretch of silence, and speaks again when the task finishes.
 */

type VoiceStatus = 'idle' | 'connecting' | 'active' | 'error';

/** Cap on one Realtime connection — voice minutes cost real money, and an
 * abandoned tab would otherwise keep billing until OpenAI's 60-min limit.
 * With a task running the session reconnects quietly instead of ending. */
const MAX_SESSION_MS = 15 * 60 * 1000;
/** Absolute cap across quiet reconnects. */
const MAX_TOTAL_MS = 50 * 60 * 1000;
/** Silence (nobody talking) while a task runs before the mic is paused. */
const IDLE_PAUSE_MS = 45 * 1000;
/** Paused this long with nothing finishing: end the voice session; the task carries on. */
const IDLE_STOP_MS = 15 * 60 * 1000;
/** How long a hang-up waits for the last utterance to finish transcribing. */
const HANGUP_TRANSCRIPT_MS = 2000;
/**
 * Hanging up mid-sentence: the speech was never committed, so it is committed on
 * hang-up and transcribed from scratch. A long monologue takes a while.
 */
const HANGUP_COMMIT_TRANSCRIPT_MS = 20000;

interface VoiceTurn {
  open: boolean;
  line: string;
  botLine: string;
  delegated: boolean;
}

const EMPTY_VOICE_TURN: VoiceTurn = { open: false, line: '', botLine: '', delegated: false };

/** Thanks, goodbye, and yes/no are not a job. Anything else the owner said is. */
function spokenWork(text: string): boolean {
  const t = text.trim().toLowerCase().replace(/[.!?,]+$/g, '').replace(/\s+/g, ' ');
  if (!t) return false;
  return !/^(thanks|thank you|thank you so much|ok|okay|k|bye|goodbye|good bye|see you|yes|no|yeah|yep|nope|nah|cool|great|perfect|sounds good|got it|alright|all right|hello|hi|hey|good morning|good afternoon|good evening|that's it|that is it|never mind|nevermind|nm|stop|cancel)$/.test(t);
}

/** A real spoken answer already covered this turn. A short "on it", or a question, did not. */
function replyFinishedTheTurn(botLine: string): boolean {
  const t = botLine.trim();
  if (!t) return false;
  // Asking for a name or a missing detail and then getting hung up on is not a finished job.
  if (t.includes('?')) return false;
  if (t.length < 240 && /\b(on it|i'm on it|i am on it|let me|i'll |i will |working on|one moment|one sec|starting|get that started|need .{0,40} first|missing piece)\b/i.test(t)) {
    return false;
  }
  return t.length > 20;
}

interface NexusHome { botId: string; chatId: string }

interface DelegatedTask {
  sessionId: string;
  chatId: string;
  startedAt: string;
  status: string;
  activity: string | null;
  lastText: string | null;
  pendingApproval: { requestId: string; approvalType: string; origin?: string; reason?: string } | null;
}

interface VoiceHelpContextValue {
  status: VoiceStatus;
  /** True while the assistant is silently working (e.g. fetching docs) before it speaks. */
  processing: boolean;
  /** True while the assistant's voice is actually audible right now. */
  speaking: boolean;
  error: string | null;
  /** A task the owner handed to the Nexus agent by voice, while it runs. */
  task: { active: boolean; activity: string | null } | null;
  /** Mic paused after a stretch of silence while the agent works. */
  listeningPaused: boolean;
  resumeListening: () => void;
  /** Returns true if the session was established. `surface: 'chat'` stays inside
   * a bot conversation and does not light up the app-wide voice control. */
  startVoice: (installedApps?: string[], businessId?: string, opts?: { quiet?: boolean; surface?: 'app' | 'chat'; botId?: string; chatId?: string; leadOpportunityId?: number; leadName?: string }) => Promise<boolean>;
  stopVoice: () => void;
  /** Where the live session is being shown. `chat` is a bot conversation. */
  surface: 'app' | 'chat' | null;
  /** Owner muted the mic from the bot voice bar. */
  micMuted: boolean;
  toggleMicMuted: () => void;
  /** Live microphone, so the voice bar can show the owner's voice. */
  micStream: MediaStream | null;
  /** The bot's spoken audio, so the voice bar can follow its voice. */
  remoteStream: MediaStream | null;
  /** A session is already running: show it as the bot conversation. */
  focusChatVoice: () => boolean;
  /** Nexus's saved voice changed. Drops any prewarmed session, and reconnects a live call so it speaks with the new voice. */
  applyVoiceChange: () => void;
  /**
   * Pre-mint the OpenAI Realtime session in the background (app open/resume)
   * so tapping the mic connects near-instantly. Free until used — tokens are
   * only charged when a session actually starts.
   */
  prewarmVoice: (installedApps?: string[], businessId?: string, opts?: { botId?: string | null; chatId?: string | null }) => void;
  /** The voice conversation in progress, so the chat can hide its lines until it ends. */
  voiceCall: { id: string; startedAt: string } | null;
  /** The bot conversation a live `chat` call belongs to. Null for the app-wide surface. */
  chatTarget: { botId: string; chatId: string | null } | null;
  /** True while the Agents page is showing the call's own voice bar, so the floating one stays hidden. */
  chatBarHosted: boolean;
  setChatBarHosted: (hosted: boolean) => void;
}

const VoiceHelpContext = createContext<VoiceHelpContextValue>({
  status: 'idle',
  processing: false,
  speaking: false,
  error: null,
  task: null,
  listeningPaused: false,
  resumeListening: () => {},
  startVoice: async () => false,
  stopVoice: () => {},
  surface: null,
  micMuted: false,
  toggleMicMuted: () => {},
  micStream: null,
  remoteStream: null,
  focusChatVoice: () => false,
  applyVoiceChange: () => {},
  prewarmVoice: () => {},
  voiceCall: null,
  chatTarget: null,
  chatBarHosted: false,
  setChatBarHosted: () => {},
});

const DELEGATION_TOOL_NAMES = new Set(['delegate_task', 'check_delegated_task', 'stop_delegated_task', 'answer_team_request']);

export const useVoiceHelp = () => useContext(VoiceHelpContext);

/** Fullscreen outline that pulses with the assistant's voice. */
function VoiceModeOverlay({ stream, active }: { stream: MediaStream | null; active: boolean }) {
  const ringRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active || !stream || !ringRef.current) return;

    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.85;
    source.connect(analyser);

    const data = new Uint8Array(analyser.frequencyBinCount);
    let raf = 0;
    let smoothed = 0;

    const tick = () => {
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      const level = Math.min(1, (sum / data.length) / 96); // 0..1 voice energy
      smoothed = smoothed * 0.8 + level * 0.2;

      const el = ringRef.current;
      if (el) {
        // Baseline subtle glow; grows and brightens as the AI speaks
        const spread = 3 + smoothed * 14;
        const blur = 14 + smoothed * 30;
        const alpha = 0.28 + smoothed * 0.5;
        el.style.boxShadow = `inset 0 0 ${blur}px ${spread}px rgba(59, 130, 246, ${alpha})`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      try { source.disconnect(); } catch { /* already closed */ }
      void ctx.close().catch(() => {});
    };
  }, [active, stream]);

  if (!active) return null;

  return (
    <div
      ref={ringRef}
      aria-hidden
      data-assistant-capture-ignore="true"
      className="fixed inset-0 pointer-events-none animate-voice-breathe"
      style={{
        zIndex: 2147483000,
        boxShadow: 'inset 0 0 14px 3px rgba(59, 130, 246, 0.28)',
        borderRadius: 'env(safe-area-inset-top, 0px)',
      }}
    />
  );
}

export function VoiceHelpProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [processing, setProcessing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [micStream, setMicStream] = useState<MediaStream | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const installedAppsRef = useRef<string[]>([]);
  const businessIdRef = useRef<string | undefined>(undefined);

  // Nexus general-manager bridge: the home chat, the delegated task in flight,
  // and hooks into the live data channel so effects can speak into the session.
  const nexusRef = useRef<NexusHome | null>(null);
  const taskRef = useRef<DelegatedTask | null>(null);
  const [task, setTask] = useState<VoiceHelpContextValue['task']>(null);
  const watcherRef = useRef<AbortController | null>(null);
  const sendEventRef = useRef<((event: Record<string, unknown>) => void) | null>(null);
  const requestResponseRef = useRef<((payload: Record<string, unknown>) => void) | null>(null);
  const [listeningPaused, setListeningPaused] = useState(false);
  const listeningPausedRef = useRef(false);
  const [surface, setSurface] = useState<'app' | 'chat' | null>(null);
  const surfaceRef = useRef<'app' | 'chat' | null>(null);
  const [chatTarget, setChatTarget] = useState<{ botId: string; chatId: string | null } | null>(null);
  const [chatBarHosted, setChatBarHosted] = useState(false);
  const [micMuted, setMicMuted] = useState(false);
  const micMutedRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const totalStartedAtRef = useRef<number>(0);
  const reconnectingRef = useRef(false);
  const voiceCallRef = useRef<{ id: string; startedAt: string } | null>(null);
  // The speech turn in progress. A hang-up dispatches this when the voice model never did.
  const voiceTurnRef = useRef<VoiceTurn>({ ...EMPTY_VOICE_TURN });
  /** Set when the hang-up commit found nothing to transcribe, so the hang-up stops waiting. */
  const hangupNothingRef = useRef(false);
  // Survives stopVoice resetting the turn, so a late tool call cannot start the same job twice.
  const turnClaimedRef = useRef(0);
  const turnEpochRef = useRef(0);
  const stoppingVoiceRef = useRef(false);
  /** Bumped on hang-up so a start that is still connecting cannot revive the call. */
  const voiceGenRef = useRef(0);
  /** Set for an in-chat call with one bot. Empty for the app voice button, which is always Nexus. */
  const voiceBotIdRef = useRef<string | null>(null);
  const voiceChatIdRef = useRef<string | null>(null);
  /** Set when voice started from one lead card. That lead stays in front for the whole call. */
  const leadFocusRef = useRef<{ opportunityId: number; name: string } | null>(null);
  const cardWorkIdRef = useRef<number | null>(null);
  const [voiceCall, setVoiceCall] = useState<{ id: string; startedAt: string } | null>(null);

  // Prewarmed (unbilled) Realtime session, minted ahead of the mic tap. The
  // OpenAI secret lives 10 minutes; treat it as stale a bit early.
  // Pre-minted sessions, one per target: the app button (no bot) and any bot
  // chat the owner has open. Keyed so the app refresh never evicts a chat ticket.
  const prewarmsRef = useRef(new Map<string, { secret: string; businessId: string; botId: string | null; chatId: string | null; expiresAtMs: number; nexus: NexusHome | null; epoch: number }>());
  const voiceEpochRef = useRef(0);
  const prewarmInflightRef = useRef(new Map<string, Promise<void>>());
  const prewarmKey = (businessId: string, botId?: string | null, chatId?: string | null) => `${businessId}:${botId || ''}:${chatId || ''}`;

  const publishTask = (next: DelegatedTask | null) => {
    taskRef.current = next;
    setTask(next ? { active: true, activity: next.activity } : null);
  };

  const leadFields = () => {
    const lead = leadFocusRef.current;
    return lead ? { leadOpportunityId: lead.opportunityId, leadName: lead.name } : {};
  };
  const beginCardWork = () => {
    const id = leadFocusRef.current?.opportunityId;
    if (!id) return;
    cardWorkIdRef.current = id;
    markSalesCardWorking(id);
  };
  const finishCardWork = () => {
    const id = cardWorkIdRef.current;
    cardWorkIdRef.current = null;
    if (id) settleSalesCard(id);
  };

  const applyMicTrack = (listening: boolean) => {
    const enabled = listening && !micMutedRef.current;
    micRef.current?.getAudioTracks().forEach((t) => { t.enabled = enabled; });
  };

  const setMicEnabled = (enabled: boolean) => {
    listeningPausedRef.current = !enabled;
    setListeningPaused(!enabled);
    applyMicTrack(enabled);
  };

  const toggleMicMuted = useCallback(() => {
    const next = !micMutedRef.current;
    micMutedRef.current = next;
    setMicMuted(next);
    applyMicTrack(!listeningPausedRef.current);
  }, []);

  const focusChatVoice = useCallback(() => {
    if (!pcRef.current) return false;
    surfaceRef.current = 'chat';
    setSurface('chat');
    return true;
  }, []);

  const clearIdleTimers = () => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (idleStopTimerRef.current) { clearTimeout(idleStopTimerRef.current); idleStopTimerRef.current = null; }
  };

  // Somebody spoke (or the session just started): the mic stays live, and the
  // idle countdown restarts. Only a running task ever pauses the mic.
  const noteActivity = () => {
    clearIdleTimers();
    if (!taskRef.current || !pcRef.current) return;
    idleTimerRef.current = setTimeout(() => {
      if (!taskRef.current || !pcRef.current) return;
      setMicEnabled(false);
      idleStopTimerRef.current = setTimeout(() => {
        if (listeningPausedRef.current && pcRef.current) stopVoiceRef.current();
      }, IDLE_STOP_MS);
    }, IDLE_PAUSE_MS);
  };
  const stopVoiceRef = useRef<() => void>(() => {});

  // Say something into the live session on the owner's behalf of the system
  // (task finished, team needs a decision). Barge-in rules apply: the request
  // is queued if a response is active.
  const injectSystemNote = (text: string, instructions: string) => {
    const send = sendEventRef.current;
    const request = requestResponseRef.current;
    if (!send || !request) return;
    if (listeningPausedRef.current) setMicEnabled(true);
    noteActivity();
    send({
      type: 'conversation.item.create',
      item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: `[System note from your agent team, not spoken by the owner] ${text}` }] },
    });
    request({ response: { instructions } });
  };

  const stopWatcher = () => {
    watcherRef.current?.abort();
    watcherRef.current = null;
  };

  // Last thing the agent said in the home chat since the task started, plus any
  // files or images it produced, for the spoken wrap-up.
  const taskResult = async (t: DelegatedTask): Promise<string> => {
    const { data: cards } = await supabase.from('agent_messages')
      .select('content, meta')
      .eq('chat_id', t.chatId).eq('kind', 'approval').gt('created_at', t.startedAt)
      .order('created_at', { ascending: false }).limit(5);
    const typed = (cards || []) as Array<{ meta?: { approval_type?: string; channel?: string; name?: string; drafts?: unknown[] } }>;
    const followUps = typed.find((row) => row.meta?.approval_type === 'crm_follow_ups');
    if (followUps) {
      const n = Array.isArray(followUps.meta?.drafts) ? followUps.meta.drafts.length : 0;
      return n === 1
        ? 'A follow-up draft for one lead is in the chat as a card. The owner sends it from the card. Do not read the message.'
        : `Follow-up drafts for ${n} leads are in the chat on one card. The owner flips through them there and presses Send all. Do not read the messages.`;
    }
    const draft = typed.find((row) => row.meta?.approval_type === 'send_message');
    if (draft) {
      const channel = draft.meta?.channel === 'sms' ? 'text' : 'email';
      const name = draft.meta?.name || 'them';
      return `A draft ${channel} to ${name} is in the chat as a card. The owner sends or copies it from the card. Do not read the subject or the body.`;
    }
    const { data } = await supabase.from('agent_messages')
      .select('author, kind, content, attachments, created_at')
      .eq('chat_id', t.chatId).eq('author', 'bot').gt('created_at', t.startedAt)
      .in('kind', ['text', 'image', 'file'])
      .order('created_at', { ascending: false }).limit(8);
    const rows = (data || []) as Array<{ kind: string; content: string; attachments: Array<{ name?: string }> }>;
    const text = rows.find((r) => r.kind === 'text')?.content?.trim() || t.lastText || '';
    const made = rows.filter((r) => r.kind !== 'text').flatMap((r) => (r.attachments || []).map((a) => a.name).filter(Boolean));
    const files = made.length ? ` Files made: ${made.join(', ')} (they are in the Nexus chat under Agents).` : '';
    return (text ? text.slice(0, 1200) : 'The task finished with no written summary.') + files;
  };

  // Follow the delegated session's event stream so completions and approvals
  // reach the voice the moment they happen, whether or not the Agents area is open.
  // A turn can end while a teammate still owes Nexus a reply; the task is not done then.
  const handoffsOutstanding = async (t: DelegatedTask): Promise<boolean> => {
    const { data } = await supabase.from('agent_messages')
      .select('meta').eq('chat_id', t.chatId).eq('kind', 'handoff').gt('created_at', t.startedAt);
    return ((data || []) as Array<{ meta: { direction?: string; status?: string } }>)
      .some((m) => m.meta?.direction === 'sent' && m.meta?.status === 'working');
  };

  const watchTask = (sessionId: string) => {
    stopWatcher();
    const abort = new AbortController();
    watcherRef.current = abort;
    void (async () => {
      while (!abort.signal.aborted) {
        // One hub window at a time; the window is cut short as soon as a turn ends so
        // the result is spoken right away instead of when the window times out.
        const window = new AbortController();
        const onOuterAbort = () => window.abort();
        abort.signal.addEventListener('abort', onOuterAbort, { once: true });
        const ended: { kind: 'done' | 'error' | null } = { kind: null };
        try {
          await watchSession(sessionId, (event: LabEvent) => {
            const t = taskRef.current;
            if (!t || t.sessionId !== sessionId) { window.abort(); return; }
            if (event.kind === 'status') {
              publishTask({ ...t, status: String(event.state), activity: typeof event.label === 'string' ? event.label : t.activity });
            } else if (event.kind === 'text' && typeof event.text === 'string') {
              taskRef.current = { ...t, lastText: event.text };
            } else if (event.kind === 'approval') {
              const approval = {
                requestId: String(event.requestId || ''),
                approvalType: String(event.approvalType || 'browser_origin_access'),
                origin: typeof event.origin === 'string' ? event.origin : undefined,
                reason: typeof event.reason === 'string' ? event.reason : undefined,
              };
              publishTask({ ...t, pendingApproval: approval });
              injectSystemNote(
                `The team needs the owner's permission to continue: ${approval.reason || `open ${approval.origin || 'a website'}`}. Ask the owner in one short sentence, then call answer_team_request with their answer.`,
                'Put the permission question to the owner in one short sentence and wait for their answer. Do not decide for them.',
              );
            } else if (event.kind === 'done' || event.kind === 'error') {
              if (event.kind === 'error') {
                taskRef.current = { ...t, lastText: `It hit a problem: ${String(event.message || 'the agent stopped')}` };
              }
              ended.kind = event.kind;
              window.abort();
            }
            // 'reconnect': the hub closes its window; the loop opens the next one.
          }, window.signal);
        } catch (err) {
          if (!window.signal.aborted) {
            console.warn('[voice] task watch failed:', err);
            await new Promise((r) => setTimeout(r, 2000));
          }
        } finally {
          abort.signal.removeEventListener('abort', onOuterAbort);
        }
        if (abort.signal.aborted) return;
        const t = taskRef.current;
        if (!t || t.sessionId !== sessionId) return;
        if (!ended.kind) continue;

        if (ended.kind === 'done' && !t.lastText && Date.now() - new Date(t.startedAt).getTime() < 90_000) {
          // A "done" with nothing said this early is the hub seeing an idle session before
          // the turn started. Keep following; the real completion comes through next.
          const { count } = await supabase.from('agent_messages')
            .select('id', { count: 'exact', head: true })
            .eq('chat_id', t.chatId).eq('author', 'bot').gt('created_at', t.startedAt);
          if (!count) { await new Promise((r) => setTimeout(r, 1500)); continue; }
        }

        if (ended.kind === 'done' && await handoffsOutstanding(t)) {
          // Nexus handed part of the work to a teammate and is waiting on them.
          publishTask({ ...t, status: 'waiting', activity: 'Waiting on a teammate' });
          if (t.lastText) {
            injectSystemNote(
              `Progress update from the task (not finished yet, a teammate is still working): ${t.lastText.slice(0, 600)}`,
              'Give the owner this progress update in one short spoken sentence and make clear you will tell them when it is done.',
            );
          }
          continue;
        }

        const result = await taskResult(t);
        publishTask(null);
        stopWatcher();
        finishCardWork();
        injectSystemNote(
          `The task is finished. Result: ${result}`,
          'The background task just finished. Tell the owner the result in one or two natural spoken sentences (no markdown, no lists, no filenames unless they matter), then offer the obvious next step in a few words. If the result asks the owner a question, put that question to them. If a draft card is in the chat, say only who it is for and that it is in the chat. Never read the subject or the body.',
        );
        return;
      }
    })();
  };

  const sessionBody = (installedApps: string[], businessId?: string) => ({
    installedApps,
    businessId: businessId || undefined,
    ...(voiceBotIdRef.current ? { botId: voiceBotIdRef.current } : {}),
    ...(voiceChatIdRef.current ? { chatId: voiceChatIdRef.current } : {}),
    ...leadFields(),
    actionCatalog: getAssistantActionCatalog(),
    // The model has no clock — this grounds "tomorrow" / "march 3rd"
    clientNow: new Date().toLocaleString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }),
  });

  // Mint a session ahead of the tap. No bot = the app button (always Nexus);
  // a bot/chat pair = the voice button inside that chat. Minting is free on
  // both sides (OpenAI bills talking, we bill per turn), so an unused ticket
  // just expires.
  const prewarmVoice = useCallback((installedApps: string[] = [], businessId?: string, opts: { botId?: string | null; chatId?: string | null } = {}) => {
    if (!businessId || pcRef.current) return;
    const botId = opts.botId || null;
    const chatId = botId ? opts.chatId || null : null;
    const key = prewarmKey(businessId, botId, chatId);
    if (prewarmInflightRef.current.has(key)) return;
    const cached = prewarmsRef.current.get(key);
    // 150s threshold pairs with the FAB's 4-min refresh tick so the cache is
    // re-minted BEFORE it expires — a tap should never catch it stale.
    if (cached && cached.epoch === voiceEpochRef.current && cached.expiresAtMs - Date.now() > 150_000) return;

    const epoch = voiceEpochRef.current;
    prewarmInflightRef.current.set(key, (async () => {
      try {
        const { data } = await supabase.functions.invoke('ai-voice-session', {
          body: { ...sessionBody(installedApps, businessId), botId: botId || undefined, chatId: chatId || undefined, action: 'prewarm' },
        });
        if (data?.clientSecret && epoch === voiceEpochRef.current) {
          const expiresAtMs = data.expiresAt
            ? Number(data.expiresAt) * 1000
            : Date.now() + 9 * 60 * 1000;
          prewarmsRef.current.set(key, { secret: data.clientSecret, businessId, botId, chatId, expiresAtMs, nexus: data.nexus || null, epoch });
          // Keep the map small: drop anything already expired.
          for (const [k, v] of prewarmsRef.current) if (v.expiresAtMs <= Date.now()) prewarmsRef.current.delete(k);
        }
      } catch { /* best-effort — startVoice mints its own if this failed */ }
      finally {
        prewarmInflightRef.current.delete(key);
      }
    })());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Tear down the connection. A quiet reconnect keeps the delegated task and its
  // watcher; a real stop lets the task carry on in the Agents area but stops following it.
  const cleanup = useCallback((opts: { keepTask?: boolean } = {}) => {
    try { pcRef.current?.close(); } catch { /* already closed */ }
    pcRef.current = null;
    dcRef.current = null;
    sendEventRef.current = null;
    requestResponseRef.current = null;
    micRef.current?.getTracks().forEach((t) => t.stop());
    micRef.current = null;
    setMicStream(null);
    playRemoteVoice(null);
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
    clearIdleTimers();
    listeningPausedRef.current = false;
    setListeningPaused(false);
    if (!opts.keepTask) {
      stopWatcher();
      publishTask(null);
      totalStartedAtRef.current = 0;
    }
    setRemoteStream(null);
    setProcessing(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // One record for the whole conversation, including quiet reconnects that swap
  // the realtime session. Cleared only when the owner is actually done.
  const closeVoiceCall = useCallback(() => {
    const call = voiceCallRef.current;
    if (!call) return;
    voiceCallRef.current = null;
    voiceBotIdRef.current = null;
    voiceChatIdRef.current = null;
    leadFocusRef.current = null;
    setSalesCardCall(null);
    setVoiceCall(null);
    const home = nexusRef.current;
    if (!home) return;
    const endedAt = new Date().toISOString();
    // Tell the open chat right away so the call card appears the moment the
    // owner hangs up; the server stamp below catches up behind it.
    window.dispatchEvent(new CustomEvent('agents:voice-ended', {
      detail: { chatId: home.chatId, callId: call.id, startedAt: call.startedAt, endedAt },
    }));
    void hub({
      action: 'end_voice',
      chatId: home.chatId,
      callId: call.id,
      startedAt: call.startedAt,
      endedAt,
    }).catch(() => {});
  }, []);

  const stopVoice = useCallback(() => {
    voiceGenRef.current += 1;
    // Switch the card from the call to “updating” before anything awaits, or the
    // draft flashes through while the last line is still being transcribed.
    const cardId = leadFocusRef.current?.opportunityId;
    if (cardId && !stoppingVoiceRef.current) {
      cardWorkIdRef.current = cardId;
      showSalesCardUpdating(cardId);
    } else {
      setSalesCardCall(null);
    }
    setRemoteVoiceMuted(true);
    if (stoppingVoiceRef.current) {
      cleanup();
      closeVoiceCall();
      setStatus('idle');
      stoppingVoiceRef.current = false;
      return;
    }
    stoppingVoiceRef.current = true;
    const turn = voiceTurnRef.current;
    const home = nexusRef.current;
    const call = voiceCallRef.current;
    const pending = turn.open && !turn.delegated && !!home && !!call;
    const known = turn.line.trim();
    const wantsWork = pending && (!known || (spokenWork(known) && !replyFinishedTheTurn(turn.botLine)));
    if (wantsWork && home) {
      window.dispatchEvent(new CustomEvent('agents:voice-work', { detail: { chatId: home.chatId, botId: home.botId } }));
      window.dispatchEvent(new CustomEvent('agents:expect-work'));
    }
    // The call UI goes away now. The data channel stays up only long enough to
    // catch a transcript that has not landed yet.
    surfaceRef.current = null;
    setSurface(null);
    setChatTarget(null);
    micMutedRef.current = false;
    setMicMuted(false);
    setStatus('idle');
    setError(null);
    applyMicTrack(false);
    setRemoteVoiceMuted(true);

    const clearCue = () => {
      if (!wantsWork || !home) return;
      window.dispatchEvent(new CustomEvent('agents:voice-work-clear', { detail: { chatId: home.chatId } }));
      window.dispatchEvent(new CustomEvent('agents:expect-work-clear'));
    };
    const finish = () => {
      closeVoiceCall();
      cleanup();
      setRemoteVoiceMuted(false);
      voiceTurnRef.current = { ...EMPTY_VOICE_TURN };
      stoppingVoiceRef.current = false;
      // Ready the next call on the same surface: the chat just left, or the app button.
      if (businessIdRef.current) prewarmVoice(installedAppsRef.current, businessIdRef.current, { botId: voiceBotIdRef.current, chatId: voiceChatIdRef.current });
    };
    const dispatchLine = async (line: string) => {
      if (!home || !call) return;
      if (turnClaimedRef.current || voiceTurnRef.current.delegated) return;
      const token = ++turnEpochRef.current;
      turnClaimedRef.current = token;
      voiceTurnRef.current.delegated = true;
      const card = leadFields();
      beginCardWork();
      try {
        const { sessionId } = await hub<{ sessionId: string }>({
          action: 'send',
          chatId: home.chatId,
          message: line,
          source: 'voice',
          callId: call.id,
          startedAt: call.startedAt,
          ...card,
        });
        window.dispatchEvent(new CustomEvent('agents:sessions-refresh'));
        watchTask(sessionId);
      } catch {
        if (turnClaimedRef.current !== token) return;
        turnClaimedRef.current = 0;
        voiceTurnRef.current.delegated = false;
        finishCardWork();
        clearCue();
      }
    };

    const dropUpdatingIfIdle = () => {
      if (voiceTurnRef.current.delegated || turnClaimedRef.current) return;
      finishCardWork();
    };
    if (!pending) {
      dropUpdatingIfIdle();
      finish();
      return;
    }
    if (known) {
      if (spokenWork(known) && !replyFinishedTheTurn(turn.botLine)) void dispatchLine(known);
      else {
        clearCue();
        dropUpdatingIfIdle();
      }
      finish();
      return;
    }
    // Semantic VAD only commits speech once the owner pauses. Someone who talks
    // straight through and hangs up leaves it all in the input buffer, where it is
    // never transcribed. Commit it now; the brief delay lets the last words arrive.
    const send = sendEventRef.current;
    let committed = false;
    hangupNothingRef.current = false;
    if (send) {
      committed = true;
      window.setTimeout(() => send({ type: 'input_audio_buffer.commit' }), 250);
    }
    void (async () => {
      const started = Date.now();
      const waitMs = committed ? HANGUP_COMMIT_TRANSCRIPT_MS : HANGUP_TRANSCRIPT_MS;
      while (
        !voiceTurnRef.current.line.trim()
        && !voiceTurnRef.current.delegated
        && !(hangupNothingRef.current && Date.now() - started >= HANGUP_TRANSCRIPT_MS)
        && Date.now() - started < waitMs
      ) {
        await new Promise((resolve) => setTimeout(resolve, 80));
      }
      const latest = voiceTurnRef.current;
      const line = latest.line.trim();
      if (latest.delegated) {
        // The voice model handed it off in this short window.
      } else if (line && spokenWork(line) && !replyFinishedTheTurn(latest.botLine)) {
        void dispatchLine(line);
      } else {
        clearCue();
        dropUpdatingIfIdle();
      }
      finish();
    })();
  }, [cleanup, closeVoiceCall, prewarmVoice]);
  stopVoiceRef.current = stopVoice;

  const resumeListening = useCallback(() => {
    if (!pcRef.current) return;
    setMicEnabled(true);
    noteActivity();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startVoice = useCallback(async (installedApps: string[] = [], businessId?: string, opts: { quiet?: boolean; surface?: 'app' | 'chat'; botId?: string; chatId?: string; leadOpportunityId?: number; leadName?: string } = {}) => {
    if (pcRef.current) return true; // already running
    const gen = voiceGenRef.current;
    if (!opts.quiet) {
      voiceBotIdRef.current = opts.surface === 'chat' ? (opts.botId || voiceBotIdRef.current) : null;
      voiceChatIdRef.current = opts.surface === 'chat' ? (opts.chatId || voiceChatIdRef.current) : null;
      const leadId = Number(opts.leadOpportunityId);
      leadFocusRef.current = Number.isFinite(leadId) && leadId > 0
        ? { opportunityId: Math.round(leadId), name: String(opts.leadName || '').replace(/\s+/g, ' ').trim().slice(0, 80) }
        : null;
      setSalesCardCall(leadFocusRef.current?.opportunityId ?? null);
    }
    if (!voiceCallRef.current) {
      const call = { id: crypto.randomUUID(), startedAt: new Date().toISOString() };
      voiceCallRef.current = call;
      setVoiceCall(call);
      voiceTurnRef.current = { ...EMPTY_VOICE_TURN };
      turnClaimedRef.current = 0;
    }
    // Quiet starts are silent reconnects mid-call: no chimes for those.
    const announce = !opts.quiet;
    if (announce) {
      setStatus('connecting');
      playVoiceChime('start'); // inside the tap that opened the call, so audio is allowed
      setRemoteVoiceMuted(false);
      const nextSurface = opts.surface === 'chat' ? 'chat' : 'app';
      surfaceRef.current = nextSurface;
      setSurface(nextSurface);
      setChatTarget(nextSurface === 'chat' && voiceBotIdRef.current ? { botId: voiceBotIdRef.current, chatId: voiceChatIdRef.current } : null);
      micMutedRef.current = false;
      setMicMuted(false);
    }
    setError(null);

    installedAppsRef.current = installedApps;
    businessIdRef.current = businessId || undefined;
    if (!opts.quiet || !totalStartedAtRef.current) totalStartedAtRef.current = Date.now();

    try {
      // 1. Ephemeral session token — the fast path uses the PREWARMED mint
      // (created on app open/resume), so the only remaining work is the SDP
      // exchange + ICE. Billing is per turn on real usage (see reportUsage), so
      // consuming a prewarmed session only re-checks the balance floor. No
      // prewarm → mint now (fired WITHOUT await so it runs while the mic + peer
      // connection get ready locally).
      const consumeToken = async (): Promise<{ data: any; error: any }> => {
        // A lead-card call has to be minted with that lead in the instructions.
        // A prewarmed chat session was minted without it.
        if (leadFocusRef.current) {
          return supabase.functions.invoke('ai-voice-session', { body: sessionBody(installedApps, businessId) });
        }
        const key = businessId ? prewarmKey(businessId, voiceBotIdRef.current, voiceBotIdRef.current ? voiceChatIdRef.current : null) : '';
        const inflight = prewarmInflightRef.current.get(key);
        if (inflight) await inflight.catch(() => {});
        const cached = prewarmsRef.current.get(key);
        if (cached && cached.epoch === voiceEpochRef.current && cached.expiresAtMs - Date.now() > 30_000) {
          prewarmsRef.current.delete(key); // single-use
          void supabase.functions
            .invoke('ai-voice-session', { body: { action: 'charge', businessId } })
            .then(({ data: chargeData }) => {
              if (chargeData?.insufficientTokens) {
                cleanup();
                closeVoiceCall();
                setStatus('error');
                setError(chargeData?.error || 'Not enough AI tokens to start voice mode — purchase more in AI Settings.');
              }
            })
            .catch(() => { /* transient network failure — session already running */ });
          return { data: { clientSecret: cached.secret, nexus: cached.nexus }, error: null };
        }
        return supabase.functions.invoke('ai-voice-session', {
          body: sessionBody(installedApps, businessId),
        });
      };
      const tStart = performance.now();
      const marks: Record<string, number> = {};
      const mark = (name: string) => { marks[name] = Math.round(performance.now() - tStart); };
      const tokenPromise = consumeToken().then((r) => { mark('token'); return r; });

      // 2. Microphone — started here but NOT awaited: the track is attached
      // via replaceTrack once it arrives (no renegotiation needed), so mic
      // acquisition runs in parallel with the whole network handshake instead
      // of blocking it. Echo cancellation is critical: without it the mic
      // picks up the AI's own voice from the speakers and the model thinks
      // it's being interrupted, cutting itself off mid-sentence
      const micPromise = navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // 3. WebRTC peer connection to OpenAI Realtime
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      pc.ontrack = (e) => {
        if (e.track.kind !== 'audio') return;
        const stream = e.streams[0]?.getAudioTracks().length
          ? new MediaStream(e.streams[0].getAudioTracks())
          : new MediaStream([e.track]);
        setRemoteStream(stream);
        // Play through Web Audio. An <audio> element here is the black box iOS draws.
        playRemoteVoice(stream);
      };

      // "Active" needs BOTH the connection and the mic: flipping on connect
      // alone invites the user to talk into a mic that isn't attached yet.
      // In practice the mic (permission already granted) lands well before ICE.
      let micAttached = false;
      let pcConnected = false;
      let chimedConnected = false;
      const maybeActivate = () => {
        if (micAttached && pcConnected && pcRef.current === pc) {
          setStatus('active');
          if (announce && !chimedConnected) { chimedConnected = true; playVoiceChime('connected'); }
          console.info(
            `[voice] connected in ${Math.round(performance.now() - tStart)}ms`,
            { ...marks, prewarmed: marks.token !== undefined && marks.token < 100 },
          );
        }
      };

      pc.onconnectionstatechange = () => {
        const state = pc.connectionState;
        if (state === 'connected') {
          mark('ice');
          pcConnected = true;
          maybeActivate();
        }
        if (state === 'failed' || state === 'disconnected' || state === 'closed') {
          if (pcRef.current === pc) {
            const reconnecting = reconnectingRef.current;
            cleanup();
            setStatus((prev) => (prev === 'active' || prev === 'connecting' ? 'idle' : prev));
            if (!reconnecting) closeVoiceCall();
          }
        }
      };

      // The audio slot goes into the offer now; the real track drops in later.
      const audioTransceiver = pc.addTransceiver('audio', { direction: 'sendrecv' });
      micPromise
        .then((mic) => {
          if (pcRef.current !== pc) {
            mic.getTracks().forEach((t) => t.stop());
            return;
          }
          micRef.current = mic;
          setMicStream(mic);
          void audioTransceiver.sender.replaceTrack(mic.getAudioTracks()[0] ?? null);
          mark('mic');
          micAttached = true;
          maybeActivate();
        })
        .catch((err) => {
          if (pcRef.current !== pc) return;
          console.error('Voice mic acquisition failed:', err);
          cleanup();
          closeVoiceCall();
          setError(
            err instanceof DOMException && err.name === 'NotAllowedError'
              ? 'Microphone access was denied. Allow the microphone to use voice mode.'
              : 'Could not access the microphone. Please try again.',
          );
          setStatus('error');
        });

      // Events channel: used to serve the model's lookup_help tool calls so
      // the assistant can pull the FULL help docs for any topic on demand
      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;
      // response.create sent while a response is still active gets REJECTED by
      // the Realtime API — on long tool chains (multi-offer proposals) that
      // silently stalls the whole flow. Serialize: while a response is active,
      // hold the request and fire it on response.done.
      let responseActive = false;
      let pendingResponseCreate: string | null = null;
      // Draft-approval gate for send_message: reading a thread and sending into
      // it within the same user turn means the wording is the model's own
      // draft — the user never approved it. Reset whenever the user speaks.
      let readThreadThisTurn = false;
      let delegatedThisTurn = false;
      let lastOwnerLine = '';
      const asksForDraft = (text: string) => /\b(re-?draft|rewrite|draft|write)\b/i.test(text) && /\b(e-?mails?|texts?|messages?)\b/i.test(text);
      const soundsLikeDraft = (text: string) => text.length > 140 && /\bsubject\b/i.test(text);
      const requestResponse = (payload: Record<string, unknown>) => {
        const msg = JSON.stringify({ type: 'response.create', ...payload });
        if (responseActive) {
          pendingResponseCreate = msg; // latest wins — it carries the newest instructions
        } else if (dc.readyState === 'open') {
          responseActive = true;
          dc.send(msg);
        }
      };
      sendEventRef.current = (event) => { if (dc.readyState === 'open') dc.send(JSON.stringify(event)); };
      requestResponseRef.current = requestResponse;
      dc.onopen = () => { noteActivity(); };

      // Everything said out loud lands in the Nexus chat, so the owner can read it
      // back and the next agent session remembers it.
      const logVoice = (author: 'user' | 'bot', text: string) => {
        const home = nexusRef.current;
        const clean = text.trim();
        if (!home || !clean) return;
        const call = voiceCallRef.current;
        void hub<{ id: string; row?: Record<string, unknown> }>({
          action: 'log_voice',
          chatId: home.chatId,
          author,
          text: clean,
          callId: call?.id,
          startedAt: call?.startedAt,
          ...(author === 'user' ? leadFields() : {}),
        }).then((saved) => {
          // Paint the saved line into the open chat now, like a typed message.
          if (saved?.row) window.dispatchEvent(new CustomEvent('agents:voice-line', { detail: { row: saved.row } }));
        }).catch(() => {});
      };

      // Exact billing: every bot reply and every transcribed owner utterance
      // carries OpenAI's usage counts. Post them as they arrive; the server
      // prices them (list cost x 1.5) and the charge key makes retries harmless.
      // When the balance is gone the server says so and the call ends.
      const reportUsage = (kind: 'response' | 'transcription', id: string, usage: unknown) => {
        const call = voiceCallRef.current;
        if (!call || !usage || typeof usage !== 'object' || !id) return;
        void supabase.functions
          .invoke('ai-voice-session', { body: { action: 'usage', businessId, callId: call.id, entries: [{ kind, id, usage }] } })
          .then(({ data }) => {
            if (data?.insufficientTokens && pcRef.current === pc) {
              cleanup();
              closeVoiceCall();
              setError('Out of AI tokens, so the call ended. Add tokens in AI Settings.');
              setStatus('error');
            }
          })
          .catch(() => { /* transient; the next turn reports again */ });
      };

      const nexusToolOutput = async (name: string, args: Record<string, unknown>): Promise<string> => {
        const home = nexusRef.current;
        if (!home) return 'The agent team is not available for this business right now. Help the owner directly instead.';
        if (name === 'delegate_task') {
          const request = String(args.request || '').trim();
          if (!request) return 'A request is required.';
          if (turnClaimedRef.current || voiceTurnRef.current.delegated) {
            return 'Already started in the chat. Say one short line that you are on it and do not start it again.';
          }
          const token = ++turnEpochRef.current;
          turnClaimedRef.current = token;
          voiceTurnRef.current.delegated = true;
          const card = leadFields();
          beginCardWork();
          try {
            const call = voiceCallRef.current;
            const { sessionId } = await hub<{ sessionId: string }>({
              action: 'send',
              chatId: home.chatId,
              message: request,
              source: 'voice',
              callId: call?.id,
              startedAt: call?.startedAt,
              ...card,
            });
            const existing = taskRef.current;
            publishTask({
              sessionId,
              chatId: home.chatId,
              startedAt: existing && existing.sessionId === sessionId ? existing.startedAt : new Date().toISOString(),
              status: 'thinking',
              activity: 'Thinking',
              lastText: null,
              pendingApproval: null,
            });
            watchTask(sessionId);
            noteActivity();
            delegatedThisTurn = true;
            return JSON.stringify({ taskId: sessionId, status: 'started', note: card.leadOpportunityId
              ? 'Running in the background. Say one short line that the card is updating. Do not read the new message.'
              : 'Running in the background. Say one short line that you are on it and carry on; a system note will arrive when it finishes. If this is an email or text draft, say only that the draft is going into the chat. Do not read the subject or the body.' });
          } catch (err) {
            if (turnClaimedRef.current === token) {
              turnClaimedRef.current = 0;
              voiceTurnRef.current.delegated = false;
              if (card.leadOpportunityId) finishCardWork();
            }
            return `Could not start the task: ${err instanceof Error ? err.message : 'unknown error'}. Tell the owner and offer to try again.`;
          }
        }
        const t = taskRef.current;
        if (name === 'check_delegated_task') {
          if (!t) return 'No task is running. If the chat already has a result, answer from that. Creating follow-ups is a card in the chat, not a routine. Call propose_routine only when the owner asked for the work to repeat. Do not hire a bot unless the owner asked for one.';
          const { data } = await supabase.from('agent_bot_sessions').select('status, activity').eq('id', t.sessionId).maybeSingle();
          return JSON.stringify({
            status: data?.status || t.status,
            doing: data?.activity || t.activity,
            runningForSeconds: Math.round((Date.now() - new Date(t.startedAt).getTime()) / 1000),
            lastSaid: t.lastText ? t.lastText.slice(0, 500) : null,
          });
        }
        if (name === 'stop_delegated_task') {
          if (!t) return 'No task is running right now.';
          try {
            await hub({ action: 'cancel', sessionId: t.sessionId });
          } catch (err) {
            return `Could not stop it: ${err instanceof Error ? err.message : 'unknown error'}`;
          }
          stopWatcher();
          publishTask(null);
          finishCardWork();
          return 'Stopped. Confirm to the owner in a few words.';
        }
        if (name === 'answer_team_request') {
          if (!t?.pendingApproval) return 'There is no open request from the team.';
          const decision = String(args.decision || '').toLowerCase() === 'approve' ? 'approve' : 'deny';
          try {
            await hub({ action: 'approve', sessionId: t.sessionId, requestId: t.pendingApproval.requestId, approvalType: t.pendingApproval.approvalType, decision });
          } catch (err) {
            return `Could not pass that on: ${err instanceof Error ? err.message : 'unknown error'}`;
          }
          publishTask({ ...t, pendingApproval: null });
          return decision === 'approve' ? 'Approved; the team is continuing. Say so in a few words.' : 'Denied; the team will work around it or stop. Say so in a few words.';
        }
        return 'Unknown tool.';
      };

      dc.onmessage = async (e) => {
        let evt: any;
        try {
          evt = JSON.parse(e.data);
        } catch {
          return;
        }
        if (evt?.type === 'response.created') {
          responseActive = true;
        }
        if (evt?.type === 'response.done') {
          responseActive = false;
          if (evt.response?.usage) reportUsage('response', String(evt.response.id || ''), evt.response.usage);
          if (pendingResponseCreate && dc.readyState === 'open') {
            responseActive = true;
            dc.send(pendingResponseCreate);
            pendingResponseCreate = null;
          }
        }
        // Signal "working" while the assistant is silent: from the moment it
        // requests a doc lookup until its voice actually starts playing
        if (evt?.type === 'output_audio_buffer.started') {
          setProcessing(false);
          noteActivity();
        }
        if (evt?.type === 'output_audio_buffer.stopped') {
          noteActivity();
        }
        if (evt?.type === 'input_audio_buffer.speech_started') {
          // A hang-up is waiting on this turn's transcript. A new speech event must not wipe it.
          if (!stoppingVoiceRef.current) {
            readThreadThisTurn = false;
            delegatedThisTurn = false;
            turnClaimedRef.current = 0;
            voiceTurnRef.current = { open: true, line: '', botLine: '', delegated: false };
          }
          noteActivity();
        }
        if (evt?.type === 'response.done' && evt.response?.status === 'failed') {
          setProcessing(false);
        }
        if (stoppingVoiceRef.current && (
          (evt?.type === 'error' && evt.error?.code === 'input_audio_buffer_commit_empty')
          || evt?.type === 'conversation.item.input_audio_transcription.failed'
        )) {
          hangupNothingRef.current = true;
        }
        if (evt?.type === 'conversation.item.input_audio_transcription.completed' && evt.usage) {
          reportUsage('transcription', String(evt.item_id || ''), evt.usage);
        }
        if (evt?.type === 'conversation.item.input_audio_transcription.completed' && typeof evt.transcript === 'string') {
          lastOwnerLine = evt.transcript;
          if (voiceTurnRef.current.open) voiceTurnRef.current.line = evt.transcript;
          logVoice('user', evt.transcript);
        }
        if (evt?.type === 'response.output_audio_transcript.done' && typeof evt.transcript === 'string') {
          if (voiceTurnRef.current.open) voiceTurnRef.current.botLine = evt.transcript;
          logVoice('bot', evt.transcript);
          // The voice model sometimes reads a redraft aloud instead of handing it
          // to the chat. If the owner asked for a draft and nothing was delegated,
          // start that task now so the card still appears.
          if (surfaceRef.current === 'chat' && !delegatedThisTurn && !turnClaimedRef.current && !voiceTurnRef.current.delegated && asksForDraft(lastOwnerLine) && soundsLikeDraft(evt.transcript)) {
            const home = nexusRef.current;
            const call = voiceCallRef.current;
            if (home) {
              delegatedThisTurn = true;
              const token = ++turnEpochRef.current;
              turnClaimedRef.current = token;
              voiceTurnRef.current.delegated = true;
              void hub<{ sessionId: string }>({
                action: 'send',
                chatId: home.chatId,
                message: lastOwnerLine,
                source: 'voice',
                callId: call?.id,
                startedAt: call?.startedAt,
              }).then(({ sessionId }) => {
                const existing = taskRef.current;
                publishTask({
                  sessionId,
                  chatId: home.chatId,
                  startedAt: existing && existing.sessionId === sessionId ? existing.startedAt : new Date().toISOString(),
                  status: 'thinking',
                  activity: 'Thinking',
                  lastText: null,
                  pendingApproval: null,
                });
                watchTask(sessionId);
              }).catch(() => {
                if (turnClaimedRef.current !== token) return;
                turnClaimedRef.current = 0;
                voiceTurnRef.current.delegated = false;
                delegatedThisTurn = false;
              });
            }
          }
        }

        // Nexus agent bridge: hand work off, check on it, stop it, answer its requests.
        if (
          evt?.type === 'response.output_item.done' &&
          evt.item?.type === 'function_call' &&
          DELEGATION_TOOL_NAMES.has(evt.item?.name)
        ) {
          setProcessing(true);
          let args: Record<string, unknown> = {};
          try {
            args = JSON.parse(evt.item.arguments || '{}');
          } catch { /* malformed args */ }
          const output = await nexusToolOutput(evt.item.name, args);
          setProcessing(false);
          if (dc.readyState === 'open') {
            dc.send(JSON.stringify({
              type: 'conversation.item.create',
              item: { type: 'function_call_output', call_id: evt.item.call_id, output },
            }));
            requestResponse({});
          }
          return;
        }

        if (
          evt?.type === 'response.output_item.done' &&
          evt.item?.type === 'function_call' &&
          evt.item?.name === 'lookup_help'
        ) {
          setProcessing(true);
          let topic = '';
          try {
            topic = JSON.parse(evt.item.arguments || '{}').topic || '';
          } catch { /* malformed args — fall through with empty topic */ }

          let output = 'No documentation found for that topic.';
          try {
            const { data: kb } = await supabase.functions.invoke('ai-voice-session', {
              body: { action: 'knowledge', topic },
            });
            if (kb?.content) output = kb.content;
          } catch (lookupErr) {
            console.warn('lookup_help fetch failed:', lookupErr);
            output = 'The documentation lookup failed. Apologize and ask the user to try again.';
          }

          if (dc.readyState === 'open') {
            dc.send(JSON.stringify({
              type: 'conversation.item.create',
              item: {
                type: 'function_call_output',
                call_id: evt.item.call_id,
                output,
              },
            }));
            requestResponse({});
          }
          return;
        }

        // Screen sight: capture a live screenshot and inject it into the
        // session as an image so the model can see what the user sees.
        if (
          evt?.type === 'response.output_item.done' &&
          evt.item?.type === 'function_call' &&
          evt.item?.name === 'look_at_screen'
        ) {
          setProcessing(true);
          const screenshot = await captureScreenshot();

          if (dc.readyState === 'open') {
            dc.send(JSON.stringify({
              type: 'conversation.item.create',
              item: {
                type: 'function_call_output',
                call_id: evt.item.call_id,
                output: screenshot
                  ? 'Screenshot captured — it is attached as the next message. Answer from what you see in it.'
                  : 'The screenshot failed. Ask the user to describe what they see instead.',
              },
            }));
            if (screenshot) {
              dc.send(JSON.stringify({
                type: 'conversation.item.create',
                item: {
                  type: 'message',
                  role: 'user',
                  content: [{ type: 'input_image', image_url: screenshot }],
                },
              }));
            }
            requestResponse({});
          }
        }

        // App-control tools: navigate, open settings, highlight, read location.
        // Executed synchronously on the client via the shared action registry.
        if (
          evt?.type === 'response.output_item.done' &&
          evt.item?.type === 'function_call' &&
          isAssistantActionName(evt.item?.name)
        ) {
          let args: Record<string, unknown> = {};
          try {
            args = JSON.parse(evt.item.arguments || '{}');
          } catch { /* malformed args — executor reports the problem */ }

          if (evt.item.name === 'read_conversation') readThreadThisTurn = true;

          // Hard gate (the prompt rule alone gets ignored): a send composed in
          // the same turn as a thread read is an unapproved draft.
          const inAgentChat = surfaceRef.current === 'chat';
          const output =
            evt.item.name === 'send_message' && readThreadThisTurn
              ? inAgentChat
                ? 'BLOCKED: this wording is your draft. Do not send it and do not read it aloud. Call delegate_task with who it is for and what the owner asked to change. The draft card appears in the chat. Say one short line that it is in the chat.'
                : 'BLOCKED: you read the thread this turn, so this wording is YOUR draft — the user has not approved it. Do not send. Say the exact draft wording out loud and ask whether to send it; call send_message again only after the user says yes.'
              : await executeAssistantAction(evt.item.name, args, {
                  installedApps: installedAppsRef.current,
                  businessId: businessIdRef.current,
                  source: 'voice',
                });

          if (dc.readyState === 'open') {
            dc.send(JSON.stringify({
              type: 'conversation.item.create',
              item: {
                type: 'function_call_output',
                call_id: evt.item.call_id,
                output,
              },
            }));
            // Per-response instruction (stronger than the system prompt):
            // mid-task the model must keep working silently, not narrate the
            // tool result back to the user. Messages reads get their own
            // instruction: the generic "call the next tool NOW" version made
            // the model re-check the inbox and abort its own speech mid-recap
            // (start-stop-start stutter), and it kept offering to open
            // conversations nobody asked about.
            const isMessagesRead =
              evt.item.name === 'get_conversations' || evt.item.name === 'read_conversation';
            const draftException = inAgentChat
              ? '(1) drafting or rewriting an email or text — call delegate_task NOW with who it is for and the change they asked for, and do NOT read the subject or the body; the draft card appears in the chat'
              : '(1) drafting a reply — say the exact draft wording and ask whether to send it, send_message only after they approve';
            requestResponse({
              response: {
                instructions: isMessagesRead
                  ? `The tool result above already contains the complete answer. ANSWER THE QUESTION THE USER ACTUALLY ASKED — if they asked about a specific person, message, or thread, answer exactly that from the real items and NEVER recite unread totals they did not ask for. Speak it now in ONE smooth reply and STOP: ONLY for general unread/missed-message checks lead with the UNREAD TOTALS counts; for "who from?" read the UNREAD BY SENDER line; for a thread, quote/summarize the real items. Do NOT call any more tools this turn, do NOT re-check the inbox, and NEVER offer to open, read, or show a conversation unless the user explicitly asks — end on the answer itself, not a question. TWO exceptions when the user asked you to ACT on the thread: ${draftException}; (2) creating a customer — you read the thread to mine their details, so call create_customer NOW with what you found (ask only for what the person never gave) and say nothing until it is done.`
                  : 'You are mid-task. If more steps remain, call the next tool NOW and say NOTHING — no narration, no describing what just happened, no explaining how anything works. Speak ONLY if (a) you need the user to answer a real question or you are blocked — one short sentence, or (b) the ENTIRE request is now complete — one short wrap-up sentence with the outcome, no explanations. If a FORM STATUS says READY and every requested detail is done, ask in one plain sentence: "The booking\'s ready — want me to create it?" and wait. Never read tool results aloud.',
              },
            });
          }
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // 4. Token should be ready by now (it minted during the local setup above)
      const { data, error: fnError } = await tokenPromise;
      if (gen !== voiceGenRef.current || pcRef.current !== pc) return false;
      if (fnError || !data?.clientSecret) {
        // supabase-js hides the response body on non-2xx; dig out the real reason
        let detail = data?.error || '';
        let body: any = data;
        if (!detail && fnError && 'context' in fnError && fnError.context instanceof Response) {
          body = await fnError.context.json().catch(() => ({}));
          detail = body?.error || '';
        }
        if (data?.insufficientTokens || body?.insufficientTokens) {
          throw new Error(
            detail || 'Not enough AI tokens to start voice mode — purchase more in AI Settings.',
          );
        }
        throw new Error(detail || fnError?.message || 'Could not start voice session');
      }
      if (data.nexus?.chatId && data.nexus?.botId) {
        nexusRef.current = { botId: String(data.nexus.botId), chatId: String(data.nexus.chatId) };
      } else if (businessId && (!nexusRef.current || opts.quiet !== true)) {
        // Older prewarm payloads carry no home chat; resolve it in the background.
        void hub<{ botId: string; chatId: string }>({ action: 'home', businessId })
          .then((home) => { if (pcRef.current === pc) nexusRef.current = { botId: home.botId, chatId: home.chatId }; })
          .catch(() => { nexusRef.current = null; });
      }

      // 429s here are usually transient rate limits — retry once before
      // giving up, and surface the REAL reason instead of a generic message.
      let sdpResponse: Response | null = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        sdpResponse = await fetch('https://api.openai.com/v1/realtime/calls', {
          method: 'POST',
          body: offer.sdp,
          headers: {
            Authorization: `Bearer ${data.clientSecret}`,
            'Content-Type': 'application/sdp',
          },
        });
        if (sdpResponse.status !== 429 || attempt === 1) break;
        const retryAfter = Number(sdpResponse.headers.get('retry-after')) || 2;
        await new Promise((r) => setTimeout(r, Math.min(retryAfter, 5) * 1000));
      }
      if (!sdpResponse || !sdpResponse.ok) {
        const errText = sdpResponse ? await sdpResponse.text().catch(() => '') : '';
        console.error('Realtime call rejected:', sdpResponse?.status, errText);
        let apiMsg = '';
        try {
          apiMsg = JSON.parse(errText)?.error?.message || '';
        } catch { /* non-JSON body */ }
        if (sdpResponse?.status === 429) {
          throw new Error(
            /quota|billing|credit/i.test(apiMsg)
              ? 'An error has occurred. Please try again in a few minutes.'
              : 'OpenAI is rate-limiting voice connections right now — wait a few seconds and try again.',
          );
        }
        throw new Error(apiMsg ? `Voice connection was rejected: ${apiMsg.slice(0, 200)}` : 'Voice connection was rejected. Please try again.');
      }

      const answerSdp = await sdpResponse.text();
      if (gen !== voiceGenRef.current || pcRef.current !== pc) return false;
      mark('sdp');
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      if (gen !== voiceGenRef.current || pcRef.current !== pc) return false;

      maxTimerRef.current = setTimeout(() => {
        const taskRunning = !!taskRef.current;
        const total = Date.now() - totalStartedAtRef.current;
        if (taskRunning && total < MAX_TOTAL_MS && !reconnectingRef.current) {
          // The agent is still working: swap to a fresh connection without ending
          // the conversation. The new session's instructions carry the transcript.
          reconnectingRef.current = true;
          cleanup({ keepTask: true });
          void startVoiceRef.current(installedAppsRef.current, businessIdRef.current, { quiet: true })
            .then((ok) => { if (!ok) { stopWatcher(); publishTask(null); setStatus('idle'); closeVoiceCall(); } })
            .finally(() => { reconnectingRef.current = false; });
          return;
        }
        cleanup();
        setStatus('idle');
        closeVoiceCall();
      }, MAX_SESSION_MS);
      return true;
    } catch (err) {
      if (gen !== voiceGenRef.current) return false;
      console.error('Voice session failed:', err);
      cleanup();
      setError(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Microphone access was denied. Allow the microphone to use voice mode.'
          : err instanceof Error ? err.message : 'Could not start voice session'
      );
      setStatus('error');
      closeVoiceCall();
      return false;
    }
  }, [cleanup, closeVoiceCall]); // eslint-disable-line react-hooks/exhaustive-deps
  const startVoiceRef = useRef(startVoice);
  startVoiceRef.current = startVoice;

  const applyVoiceChange = useCallback(() => {
    voiceEpochRef.current += 1;
    prewarmsRef.current.clear();
    if (!pcRef.current) return;
    const apps = installedAppsRef.current.slice();
    const biz = businessIdRef.current;
    const surface = surfaceRef.current === 'chat' ? 'chat' : 'app';
    const botId = voiceBotIdRef.current || undefined;
    const chatId = voiceChatIdRef.current || undefined;
    cleanup();
    micMutedRef.current = false;
    setMicMuted(false);
    void startVoiceRef.current(apps, biz, { surface, botId, chatId });
  }, [cleanup]);

  // Never leave the mic open on unmount
  useEffect(() => () => cleanup(), [cleanup]);

  // Ghost-card taps: when the AI showed candidate customers and the user taps
  // one (or the X), relay that into the live session as a user message so the
  // model continues (or cancels) without the user having to say anything.
  useEffect(() => {
    const handler = (e: Event) => {
      const dc = dcRef.current;
      if (!dc || dc.readyState !== 'open') return;
      const choice = (e as CustomEvent).detail as GhostChoice | undefined;
      if (!choice) return;

      const text =
        choice.kind === 'selected'
          ? `[UI tap] I tapped the card for ${choice.customer.name}${choice.customer.email ? ` (${choice.customer.email})` : choice.customer.phone ? ` (${choice.customer.phone})` : ''} — customer_id ${choice.customer.id}. Use this customer and continue my ORIGINAL request, including every detail I already gave (service, price, notes...). Don't ask again.`
          : '[UI tap] I closed the customer match cards — none of them. Stop that operation, don\'t select anyone, and briefly acknowledge.';

      setProcessing(true);
      // The tap usually lands while the assistant is still asking "which
      // one?" — a response.create during an active response is rejected and
      // the session stalls until the user speaks. Barge in like a voice
      // interruption: cancel the active response and flush its audio first.
      dc.send(JSON.stringify({ type: 'response.cancel' }));
      dc.send(JSON.stringify({ type: 'output_audio_buffer.clear' }));
      dc.send(JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{ type: 'input_text', text }],
        },
      }));
      dc.send(JSON.stringify({
        type: 'response.create',
        response: {
          instructions:
            choice.kind === 'selected'
              ? 'The user tapped a card — resume the task silently with the next tool call. Say NOTHING unless you need input (one short question) or the whole request is done (one short wrap-up sentence).'
              : 'The user cancelled. Acknowledge in a few words and stop — nothing else.',
        },
      }));
    };
    window.addEventListener(GHOST_CHOICE_EVENT, handler);
    return () => window.removeEventListener(GHOST_CHOICE_EVENT, handler);
  }, []);

  // Track whether the assistant's voice is audible RIGHT NOW, so UI (the AI
  // FAB button) can show speaking vs listening. Same analyser approach as the
  // viewport ring, but reduced to a boolean with a little hysteresis so the
  // icon doesn't flicker between words.
  useEffect(() => {
    if (status !== 'active' || !remoteStream) {
      setSpeaking(false);
      return;
    }
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    const source = ctx.createMediaStreamSource(remoteStream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);

    const data = new Uint8Array(analyser.frequencyBinCount);
    let raf = 0;
    let audible = false;
    let quietSince = 0;

    const tick = (t: number) => {
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      const level = (sum / data.length) / 96;
      if (level > 0.05) {
        quietSince = 0;
        if (!audible) {
          audible = true;
          setSpeaking(true);
        }
      } else if (audible) {
        // Only drop to "listening" after ~400ms of silence (pauses between words)
        if (!quietSince) quietSince = t;
        else if (t - quietSince > 400) {
          audible = false;
          quietSince = 0;
          setSpeaking(false);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      try { source.disconnect(); } catch { /* already closed */ }
      void ctx.close().catch(() => {});
      setSpeaking(false);
    };
  }, [status, remoteStream]);

  // End the session whenever the app is backgrounded or fully closed — an
  // open mic + live billing must never keep running with the app out of view.
  useEffect(() => {
    const endSession = () => {
      if (pcRef.current) stopVoice();
    };

    const onVisibilityChange = () => {
      if (document.hidden) endSession();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', endSession);
    // A phone call (user-dialed, AI-dialed, or incoming) takes over the mic —
    // CallContext fires this the moment a call leaves idle.
    window.addEventListener('phone:call-active', endSession);

    // Native app (Capacitor): backgrounding doesn't always fire the web events
    let removeNativeListener: (() => void) | null = null;
    import('@capacitor/app')
      .then(({ App }) =>
        App.addListener('appStateChange', ({ isActive }) => {
          if (!isActive) endSession();
        }),
      )
      .then((handle) => {
        removeNativeListener = () => void handle.remove();
      })
      .catch(() => { /* web build without the plugin */ });

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', endSession);
      window.removeEventListener('phone:call-active', endSession);
      removeNativeListener?.();
    };
  }, [stopVoice]);

  return (
    <VoiceHelpContext.Provider value={{ status, processing, speaking, error, task, listeningPaused, resumeListening, startVoice, stopVoice, surface, micMuted, toggleMicMuted, micStream, remoteStream, focusChatVoice, applyVoiceChange, prewarmVoice, voiceCall, chatTarget, chatBarHosted, setChatBarHosted }}>
      {children}
      <VoiceModeOverlay stream={remoteStream} active={status === 'active' && surface !== 'chat'} />
      {status === 'active' && surface !== 'chat' && (task || listeningPaused) && (
        <div
          data-assistant-capture-ignore="true"
          className="pointer-events-none fixed inset-x-0 flex justify-center px-4"
          style={{ zIndex: 2147482000, bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5.5rem)' }}
        >
          <button
            type="button"
            onClick={listeningPaused ? resumeListening : undefined}
            className={`pointer-events-auto flex items-center gap-2 rounded-full border border-white/15 bg-zinc-900/90 px-3.5 py-2 text-[13px] text-zinc-100 shadow-xl backdrop-blur-xl ${listeningPaused ? 'cursor-pointer hover:bg-zinc-800' : 'cursor-default'}`}
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-400/20">
              <svg viewBox="0 0 24 24" className="h-3 w-3 text-amber-300" fill="currentColor" aria-hidden><path d="M12 3 2.5 20h19L12 3z" /></svg>
            </span>
            {listeningPaused ? (
              <span>Listening paused · tap to resume</span>
            ) : (
              <span className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                Nexus is working{task?.activity ? `: ${task.activity}` : '…'}
              </span>
            )}
          </button>
        </div>
      )}
    </VoiceHelpContext.Provider>
  );
}
